import {
  InfoOutlined,
  SettingsBackupRestoreOutlined,
} from '@mui/icons-material';
import { Box, Button, IconButton } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ListView from '../../../../../atoms/DataList/ListView';
import { useDebounce } from 'usehooks-ts';
import {
  DebounceTimer,
  LoadingStatus,
  ToastThemes,
} from '../../../../../constants/common';
import { formatCurrency, showToast } from '../../../../../utils/common.util';
import TryAgain from '../../../../molecules/ErrorState/error.component';
import CircularLoader from '../../../../../atoms/CircularLoader/circular-loader';
import {
  permissionsObj,
  ViewAccess,
} from '../../../../OnBoarding/Organization/RolesAndPermissions/constants/roles-permissions.constants';
import {
  getFinancialYear,
  getFormattedValues,
} from '../../../../../utils/formatter';
import { GeoTagging } from '../../../../../services/geoTagging';
import { DateRangePro } from '../../../../molecules/DateRange/DateRangePro';

const AllTDSDashboard = () => {
  const { currentUserRoleId, isMobile } = useSelector((state) => state.main);
  const [checkedItems, setCheckedItems] = useState([]);
  const [list, setList] = useState({});
  const { logoImage } = useSelector((state) => state && state.onBoarding);
  const defaultMonths = logoImage?.defaultMonths;
  const defaultTimezone = logoImage?.defaultTimezone;
  const [loading, setLoading] = useState(-1);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const paths = location.pathname.split('/');
  const moduleId = Number(paths[paths.length - 1]);
  const [searchParams, setSearchParams] = useSearchParams();
  const fy = getFinancialYear(logoImage?.payrollFY);
  const [selectedDate, setSelectedDate] = useState([fy.start, fy.end]);
  const searchQuery = searchParams?.get('q');
  const debouncedVal = useDebounce(searchQuery, DebounceTimer);

  const { compBenPermission } = useSelector((state) => state.modulePermissions);
  const { allDashboardList } =
    useSelector((state) => state.compBenBuilder) || {};

  const module = allDashboardList.find(
    (cell) => cell.id === Number(moduleId)
  )?.moduleName;

  useEffect(async () => {
    if (currentUserRoleId) {
      setLoading(LoadingStatus.Loading);
      await new GeoTagging()
        .tdslistAll(
          moduleId,
          currentUserRoleId,
          searchQuery,
          getFormattedValues('YYYY-MM-DD', selectedDate[0]),
          getFormattedValues('YYYY-MM-DD', selectedDate[1])
        )
        .then((res) => {
          setLoading(LoadingStatus.Success);
          setList(res);
        })
        .catch((err) => {
          setLoading(LoadingStatus.Failure);
          showToast(err?.response?.data?.message, ToastThemes.error);
          throw err?.response?.data?.message;
        });
    }
  }, [currentUserRoleId, moduleId, debouncedVal, selectedDate]);

  useEffect(() => {
    setCheckedItems([]);
  }, [list?.declarationsList]);

  const handleAddClick = () => {
    if (checkIfDatePassed(list?.submitBy, logoImage?.payrollFY)) {
      showToast(
        'Submission not allowed. The deadline to apply for exemption has passed. Please contact your HR department for further assistance.',
        ToastThemes.error
      );
    } else {
      navigate('create');
    }
  };

  const rowData = list?.declarationsList?.map((items) => {
    const {
      id,
      status,
      proposedAmount,
      receiptAmount,
      financialYear,
      createdBy,
      salaryComponent,
    } = items;
    return {
      empName: isMobile
        ? { hide: true }
        : compBenPermission?.[module]?.viewAccess !== ViewAccess.MY_ACCESS
        ? createdBy?.displayName || ''
        : { hide: true },
      tds: salaryComponent,
      pamt: proposedAmount
        ? formatCurrency(Number(proposedAmount), 2, 'en-IN')
        : '',
      aamt: receiptAmount
        ? formatCurrency(Number(receiptAmount), 2, 'en-IN')
        : '',
      status,
      action: (
        <IconButton
          onClick={() => {
            if (checkIfDatePassed(list?.submitBy, logoImage?.payrollFY)) {
              showToast(
                'Submission not allowed. The deadline to apply for exemption has passed. Please contact your HR department for further assistance.',
                ToastThemes.error
              );
            } else {
              navigate({ pathname: `${id}` });
            }
          }}
        >
          <InfoOutlined sx={{ color: 'primary.main80' }} />
        </IconButton>
      ),
      id: {
        value: {
          id,
          status,
        },
        hide: true,
      },
    };
  });

  const isDisabledReset = () => {
    return selectedDate[0].isSame(fy.start) && selectedDate[1].isSame(fy.end);
  };

  const resetFilters = () => {
    setSelectedDate([fy.start, fy.end]);
  };

  if (loading === LoadingStatus.Failure) {
    return <TryAgain />;
  }
  return (
    <>
      <CircularLoader show={loading === LoadingStatus.Loading} />
      <Box sx={{ backgroundColor: 'white' }}>
        <Box display="flex" justifyContent="flex-end" gap={2} mb={1} p={2}>
          <DateRangePro
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
          <Button
            startIcon={<SettingsBackupRestoreOutlined />}
            sx={{ textTransform: 'capitalize', mt: -1.5 }}
            disabled={isDisabledReset()}
            onClick={resetFilters}
          >
            Clear
          </Button>
        </Box>
        <ListView
          headCells={[
            // {
            //   id: 'referenceNumber',
            //   label: 'Reference Number',
            //   disablePadding: isMobile,
            // },
            {
              id: 'empName',
              label: 'Employee Name',
              hide:
                isMobile ||
                compBenPermission?.[module]?.viewAccess ===
                  ViewAccess.MY_ACCESS,
            },
            {
              id: 'tds',
              label: 'Expemtion Component',
            },
            {
              id: 'pamt',
              label: 'Proposed Amount',
              numeric: true,
              dataType: 'number',
            },
            {
              id: 'aamt',
              label: 'Actual Amount',
              numeric: true,
              dataType: 'number',
            },
            {
              id: 'status',
              label: 'Status',
            },
          ]}
          rowsData={rowData || []}
          showSerialNo={!isMobile}
          showCheckbox={false}
          setCheckedItems={setCheckedItems}
          checkedItems={checkedItems}
          allowSelectAll={true}
          onAddClick={handleAddClick}
          showAddIcon={
            compBenPermission?.[module]?.[permissionsObj.allowAdd] || false
          }
        />
      </Box>
    </>
  );
};
export default AllTDSDashboard;

function checkIfDatePassed(rawDate, fyStart = '0401') {
  const today = new Date();

  const targetMonth = parseInt(rawDate.slice(0, 2), 10);
  const targetDay = parseInt(rawDate.slice(2, 4), 10);

  const fyStartMonth = parseInt(fyStart.slice(0, 2), 10);
  const fyStartDay = parseInt(fyStart.slice(2, 4), 10);

  const currentFYStart =
    today.getMonth() + 1 > fyStartMonth ||
    (today.getMonth() + 1 === fyStartMonth && today.getDate() >= fyStartDay)
      ? today.getFullYear()
      : today.getFullYear() - 1;

  const targetYear =
    targetMonth > fyStartMonth ||
    (targetMonth === fyStartMonth && targetDay >= fyStartDay)
      ? currentFYStart
      : currentFYStart + 1;

  const fullTargetDate = new Date(targetYear, targetMonth - 1, targetDay);

  return today > fullTargetDate;
}
